import {ChartModel} from '@xh/hoist/cmp/chart';
import {HoistModel, LoadSpec, lookup, managed, XH} from '@xh/hoist/core';
import {DashViewModel} from '@xh/hoist/desktop/cmp/dash';
import {action, bindable, observableRef, runInAction} from '@xh/hoist/mobx';
import {first, last, sumBy} from 'lodash';
import Highcharts from 'highcharts/highstock';
import {Position} from '../../../../data/DataTypes';
import {areaGradient, BENCHMARK_COLOR, CHART_DOWN_COLOR, CHART_FLAT_COLOR, CHART_UP_COLOR} from '../../../../cmp/ChartColors';

export type PerformanceSeriesMeta = {key: string; name: string; color: string; visible: boolean};

export class HistoricPerformanceWidgetModel extends HoistModel {
    @managed chartModel = new ChartModel({highchartsConfig: this.createChartModel()});
    @lookup(DashViewModel) dashViewModel: DashViewModel;
    @observableRef accessor seriesMeta: PerformanceSeriesMeta[] = [];
    @bindable accessor logScale: boolean = false;

    override onLinked() {
        super.onLinked();

        this.addReaction({
            track: () => XH.livePositionsService.cube.store.lastUpdated,
            run: () => this.updateChart()
        });
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        const data = XH.performanceService.performanceChartData,
            // Balance is colored by its move over the whole history, as on the Portfolio tab's chart.
            startMv = first(data.marketValueData)?.[1],
            endMv = last(data.marketValueData)?.[1],
            mvColor = endMv < startMv ? CHART_DOWN_COLOR : CHART_UP_COLOR,
            isVisible = (key: string) => this.seriesMeta.find(m => m.key === key)?.visible ?? true;

        const seriesDefs: PerformanceSeriesMeta[] = [
            {key: 'mv', name: 'Balance', color: mvColor, visible: isVisible('mv')},
            {key: 'deposits', name: 'Deposits', color: CHART_FLAT_COLOR, visible: isVisible('deposits')},
            {key: 'benchmark', name: 'SPX', color: BENCHMARK_COLOR, visible: isVisible('benchmark')}
        ];

        let mvSeries = {
            id: 'mv',
            name: seriesDefs[0].name,
            type: 'area',
            color: seriesDefs[0].color,
            fillColor: areaGradient(seriesDefs[0].color, '40'),
            lineWidth: 1.5,
            threshold: null,
            zIndex: 2,
            visible: seriesDefs[0].visible,
            data: data.marketValueData
        };

        let depositSeries = {
            id: 'deposits',
            name: seriesDefs[1].name,
            type: 'line',
            dashStyle: 'shortdash',
            color: seriesDefs[1].color,
            visible: seriesDefs[1].visible,
            data: data.depositData
        };

        let benchmarkSeries = {
            id: 'benchmark',
            name: seriesDefs[2].name,
            type: 'line',
            dashStyle: 'shortdot',
            lineWidth: 1.5,
            color: seriesDefs[2].color,
            visible: seriesDefs[2].visible,
            data: data.benchmarkData
        };

        runInAction(() => {
            this.chartModel.setSeries([mvSeries, depositSeries, benchmarkSeries]);
            this.seriesMeta = seriesDefs;
        });
    }

    /** Toggles the y-axis between linear and logarithmic scales. */
    @action
    toggleLogScale() {
        this.logScale = !this.logScale;
        this.chartModel.updateHighchartsConfig({
            yAxis: {type: this.logScale ? 'logarithmic' : 'linear'}
        });
    }

    /** Toggles a series' visibility - clicked via its color-matched toolbar button. */
    @action
    toggleSeries(key: string) {
        const meta = this.seriesMeta.find(m => m.key === key);
        if (!meta) return;

        const visible = !meta.visible;
        this.seriesMeta = this.seriesMeta.map(m => (m.key === key ? {...m, visible} : m));
        (this.chartModel.highchart?.get(key) as Highcharts.Series)?.setVisible(visible, true);
    }

    async updateChart() {
        if (this.chartModel.highchart.resetZoomButton) return;

        const series = this.chartModel.highchart.series[0];
        const lastData = series.options.data[series.options.data.length - 1];
        const lastPoint = series.points[series.points.length - 1];
        const allPositions = XH.livePositionsService.cube.store.allRecords.map(r => r.data as Position);
        const liveMv = sumBy(allPositions, 'marketValue');

        lastPoint?.update([lastData[0], liveMv]);
    }

    private createChartModel() {
        return {
            chart: {zoomType: 'x', animation: false},
            legend: {enabled: false},
            xAxis: {type: 'datetime', tickPosition: 'inside', crosshair: {dashStyle: 'shortdot'}},
            // Values on the right, as on a trading chart. The axis title would only repeat the tooltip.
            yAxis: {
                opposite: true,
                tickPosition: 'inside',
                title: {text: null},
                gridLineDashStyle: 'Dot'
            },
            plotOptions: {
                series: {animation: false},
                area: {
                    marker: {enabled: false},
                    states: {
                        hover: {enabled: false}
                    }
                },
                line: {
                    marker: {enabled: false},
                    states: {
                        hover: {enabled: false}
                    }
                }
            },
            tooltip: {
                shared: true,
                valueDecimals: 0,
                valuePrefix: '$',
                xDateFormat: '%Y-%m-%d',
                pointFormat: '<span style="color:{series.color}">{series.name}</span>: <b>{point.y}</b><br/>'
            }
        };
    }
}
